import * as corgi from 'external/dev_april_corgi+/js/corgi';
import {
  Controller,
  Response,
} from 'external/dev_april_corgi+/js/corgi/controller';
import {EmptyDeps} from 'external/dev_april_corgi+/js/corgi/deps';
import {CorgiEvent} from 'external/dev_april_corgi+/js/corgi/events';
import {ACTION, CHANGED, CLOSE} from 'external/dev_april_corgi+/js/emu/events';

import {Concentration, ListInventoryResponse} from './types/custom';
import {Button, Input, Select} from './widgets';

const INVENTORY_TYPES = [
  'buffer',
  'dnaPart',
  'enzyme',
  'library',
  'oligo',
  'reagent',
] as const;

type InventoryType = (typeof INVENTORY_TYPES)[number];

interface InventoryItem {
  type: InventoryType;
  name: string;
  concentration?: Concentration;
}

interface Args {
  inventory: ListInventoryResponse;
  onSave: (inventory: ListInventoryResponse) => void;
}

interface State {
  items: InventoryItem[];
}

export class InventoryEditorController extends Controller<
  Args,
  EmptyDeps,
  HTMLElement,
  State
> {
  private readonly onSave: (inventory: ListInventoryResponse) => void;

  constructor(response: Response<InventoryEditorController>) {
    super(response);
    this.onSave = response.args.onSave;
  }

  addItem(): void {
    this.updateState({
      ...this.state,
      items: [
        ...this.state.items,
        {type: 'reagent', name: '', concentration: undefined},
      ],
    });
  }

  deleteItem(e: CorgiEvent<typeof ACTION>): void {
    const index = e.actionElement.data('index')?.number();
    if (index === undefined) return;

    const items = [...this.state.items];
    items.splice(index, 1);
    this.updateState({...this.state, items});
  }

  typeChanged(e: CorgiEvent<typeof CHANGED>): void {
    const index = e.actionElement.data('index')?.number();
    if (index === undefined) return;

    const items = [...this.state.items];
    items[index] = {...items[index], type: e.detail.value as InventoryType};
    this.updateState({...this.state, items});
  }

  nameChanged(e: CorgiEvent<typeof CHANGED>): void {
    const index = e.actionElement.data('index')?.number();
    if (index === undefined) return;

    const items = [...this.state.items];
    items[index] = {...items[index], name: e.detail.value};
    this.updateState({...this.state, items});
  }

  concentrationChanged(e: CorgiEvent<typeof CHANGED>): void {
    const index = e.actionElement.data('index')?.number();
    if (index === undefined) return;

    const items = [...this.state.items];
    const value = e.detail.value.trim();
    if (value) {
      // Parse concentration like "10 nM" or "1x"
      const match = value.match(/^([\d.]+)\s*(.*)$/);
      if (match) {
        items[index] = {
          ...items[index],
          concentration: {
            number: parseFloat(match[1]),
            unit: match[2] || 'x',
          },
        };
      }
    } else {
      items[index] = {...items[index], concentration: undefined};
    }
    this.updateState({...this.state, items});
  }

  save(): void {
    const inventory: ListInventoryResponse = {
      buffers: [],
      dnaParts: [],
      enzymes: [],
      libraries: [],
      oligos: [],
      reagents: [],
    };

    for (const item of this.state.items) {
      if (!item.name.trim()) continue;

      const entry = {
        name: item.name,
        concentration: item.concentration,
      };

      switch (item.type) {
        case 'buffer':
          inventory.buffers.push(entry);
          break;
        case 'dnaPart':
          inventory.dnaParts.push(entry);
          break;
        case 'enzyme':
          inventory.enzymes.push(entry);
          break;
        case 'library':
          inventory.libraries.push(entry);
          break;
        case 'oligo':
          inventory.oligos.push(entry);
          break;
        case 'reagent':
          inventory.reagents.push(entry);
          break;
      }
    }

    this.onSave(inventory);
    this.trigger(CLOSE, {kind: 'resolve'});
  }
}

function inventoryToItems(inventory: ListInventoryResponse): InventoryItem[] {
  const items: InventoryItem[] = [];

  for (const b of inventory.buffers) {
    items.push({
      type: 'buffer',
      name: b.name,
      concentration: b.concentration,
    });
  }
  for (const d of inventory.dnaParts) {
    items.push({
      type: 'dnaPart',
      name: d.name,
      concentration: d.concentration,
    });
  }
  for (const e of inventory.enzymes) {
    items.push({
      type: 'enzyme',
      name: e.name,
      concentration: e.concentration,
    });
  }
  for (const l of inventory.libraries) {
    items.push({
      type: 'library',
      name: l.name,
      concentration: l.concentration,
    });
  }
  for (const o of inventory.oligos) {
    items.push({type: 'oligo', name: o.name, concentration: o.concentration});
  }
  for (const r of inventory.reagents) {
    items.push({
      type: 'reagent',
      name: r.name,
      concentration: r.concentration,
    });
  }

  return items;
}

export function makeInventoryEditor(args: Args) {
  return <InventoryEditor {...args} />;
}

function InventoryEditor(
  args: Args,
  state: State | undefined,
  updateState: (newState: State) => void,
) {
  if (!state) {
    state = {
      items: inventoryToItems(args.inventory),
    };
  }

  return (
    <div
      className="bg-neutral-800 flex flex-col gap-4 max-h-[80vh] min-w-[500px] p-4 rounded"
      js={corgi.bind({
        controller: InventoryEditorController,
        args,
        state: [state, updateState],
      })}
    >
      <div className="font-semibold text-lg">Edit Inventory</div>

      <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
        {state.items.length === 0 ? (
          <div className="text-neutral-400 text-sm">
            No items. Click "Add Item" to add one.
          </div>
        ) : (
          state.items.map((item, i) => (
            <InventoryItemRow index={i} item={item} />
          ))
        )}
      </div>

      <div className="flex gap-2">
        <Button
          buttonType="secondary"
          unboundEvents={{corgi: [[ACTION, 'addItem']]}}
        >
          Add Item
        </Button>
        <div className="grow" />
        <Button unboundEvents={{corgi: [[ACTION, 'save']]}}>Save</Button>
      </div>
    </div>
  );
}

function InventoryItemRow({index, item}: {index: number; item: InventoryItem}) {
  const concentrationStr = item.concentration
    ? `${item.concentration.number} ${item.concentration.unit}`
    : '';

  return (
    <div className="flex gap-2 items-center" data={{index}}>
      <div data={{index}} unboundEvents={{corgi: [[CHANGED, 'typeChanged']]}}>
        <Select
          className="bg-neutral-700 text-sm w-28"
          options={INVENTORY_TYPES.map(t => ({
            label: typeToLabel(t),
            value: t,
            selected: t === item.type,
          }))}
        />
      </div>
      <Input
        className="bg-neutral-700 flex-1 text-sm"
        data={{index}}
        placeholder="Name"
        value={item.name}
        unboundEvents={{corgi: [[CHANGED, 'nameChanged']]}}
      />
      <Input
        className="bg-neutral-700 text-sm w-24"
        data={{index}}
        placeholder="e.g. 10 nM"
        value={concentrationStr}
        unboundEvents={{corgi: [[CHANGED, 'concentrationChanged']]}}
      />
      <div
        className="cursor-pointer"
        data={{index}}
        unboundEvents={{corgi: [[ACTION, 'deleteItem']]}}
      >
        <Button buttonType="dangerous">X</Button>
      </div>
    </div>
  );
}

function typeToLabel(type: InventoryType): string {
  switch (type) {
    case 'buffer':
      return 'Buffer';
    case 'dnaPart':
      return 'DNA Part';
    case 'enzyme':
      return 'Enzyme';
    case 'library':
      return 'Library';
    case 'oligo':
      return 'Oligo';
    case 'reagent':
      return 'Reagent';
  }
}
